<template>
  <div class="week-view w-100 py-5 px-3">
    <div class="row">
      <div class="col-lg-12 col-md-12 mx-auto"><h1 class="fw-light">Rezerwacje</h1>
        <div v-if="!this.authStore.user" class="alert alert-warning" role="alert">Rezerwacja online tylko dla zalogowanych użytkowników</div>
        <div class="row align-items-center mb-4">
          <div class="col-md-7 pricing-info">
            <p class="lead text-body-secondary mb-0" v-if="formattedPrices">
              <span class="badge p-3 me-1 bg-secondary-subtle text-bg-primary bg-pricing">Cennik</span>
              <span class="badge p-2 me-1 bg-secondary-subtle border border-secondary-subtle bg-price-value" v-for="(price, hours, index) in formattedPrices" :key="hours">
                    <span class="bg-info-subtle border border-info-subtle text-light p-3 rounded-1 bg-price-hour">{{ hours }}h<span v-if="index === Object.keys(formattedPrices).length-1">+</span></span><span class="vr mx-2"></span><span>{{ price }}zł/h</span>
                  </span>
            </p>
          </div>
          <div class="col-md-5 legend-info d-flex align-items-center justify-content-md-end mt-3 mt-md-0">
            <div class="d-flex align-items-center me-4">
              <div class="legend-box bg-danger p-2 me-1"></div>
              <span class="text-body-secondary small fw-bold text-uppercase">Niedostępne</span>
            </div>
            <div class="d-flex align-items-center">
              <div class="legend-box slot-user-reserved p-2 me-1"></div>
              <span class="text-body-secondary small fw-bold text-uppercase">Twoje rezerwacje</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
        v-for="(day, index) in days"
        :key="day.dateStr"
        class="day-section-wrapper d-flex justify-content-center"
    >
      <DaySection
          :index="index"
          :label="day.label"
          :spaces="spaces"
          :time-slots="timeSlots"
          :isLoading="isLoading"
          :cooldown-minutes="cooldownMinutes"
          :external-first-cell="globalFirstCell"
          :external-selected-range="globalSelectedRange"
          :active-section-label="globalActiveSection"
          @update-selection="handleSelectionUpdate"
          @reservation-created="handleReservationCreated"
      />
    </div>
    <FullPageAlert
        v-if="collisionAlert.show"
        :title="collisionAlert.title"
        :message="collisionAlert.message"
        @close="collisionAlert.show = false"
    />
  </div>
</template>

<script>
import DaySection from './DaySection.vue'
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";
import {useAuthStore} from "@/stores/auth";
import {usePricesStore} from "@/stores/prices";
import {fetchWrapper} from "@/wrappers/api/fetchWrapper";

export default {
  name: 'WeekView',
  components: {DaySection, FullPageAlert},
  setup() {
    const authStore = useAuthStore();
    const pricesStore = usePricesStore();
    return {authStore, pricesStore};
  },
  data() {
    return {
      spaces: [],
      startHour: 17,
      endHour: 23,
      cooldownMinutes: 30,
      isLoading: false,
      locationSpaces: [],
      globalFirstCell: null,
      globalSelectedRange: null,
      globalActiveSection: null,
      refreshTimer: null,
      allowGuestSelection: false,
      collisionAlert: {
        show: false,
        title: '',
        message: ''
      }
    }
  },
  computed: {
    formattedPrices() {
      const config = this.pricesStore.getPriceByLocationId(1);
      return config ? config.pricePerHour : null;
    },
    days() {
      const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']
      const result = []
      const now = new Date()
      const today = new Date(now)
      today.setHours(0, 0, 0, 0)

      // Determine if today should be skipped (if current hour >= endHour)
      let startOffset = 0;
      if (now.getHours() >= this.endHour) {
        startOffset = 1;
      }

      for (let i = startOffset; i < startOffset + 7; i++) {
        const date = new Date(today)
        date.setDate(today.getDate() + i)

        const name = dayNames[date.getDay()]
        const dateStr = date.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
        const isToday = i === 0 && startOffset === 0;
        const label = isToday ? `${name}, ${dateStr} (dziś)` : `${name}, ${dateStr}`

        result.push({label, dateStr})
      }

      return result
    },
    timeSlots() {
      const slots = []
      for (let h = this.startHour; h < this.endHour; h++) {
        slots.push(this.formatTime(h, 0))
        slots.push(this.formatTime(h, 30))
      }
      return slots
    },
  },
  methods: {
    formatTime(hour, minute) {
      const hh = String(hour).padStart(2, '0')
      const mm = String(minute).padStart(2, '0')
      return `${hh}:${mm}`
    },
    async loadSpaces() {
      if (this.isLoading) return;

      const isInitialLoad = this.spaces.length === 0;
      if (isInitialLoad) {
        this.isLoading = true;
      }

      try {
        let locationSpaces = this.locationSpaces;

        // Fetch spaces only if they haven't been loaded yet
        if (isInitialLoad) {
          const spacesData = await fetchWrapper.get('/spaces/location/1');
          this.locationSpaces = spacesData.locationSpaces;
          locationSpaces = this.locationSpaces;
        }

        // Always fetch reservations
        const reservationsData = await fetchWrapper.get('/reservations/location/1');
        const reservations = reservationsData.locationReservations;
        const userReservations = reservationsData.userReservations || {};

        // Define updatedSpaces here
        const updatedSpaces = locationSpaces.map(space => ({
          id: space.Id,
          locationId: space.LocationId,
          name: space.Name,
          description: space.Description,
          active: space.Active,
          reservations: this.groupReservationsByDate(reservations[space.Id] || []),
          userReservations: this.groupReservationsByDate(userReservations[space.Id] || []),
        }));

        // Update the state
        this.spaces = updatedSpaces;

        // Check for collisions with current selection
        this.checkSelectionCollision();

      } catch (error) {
        //
      } finally {
        this.isLoading = false;
        this.startPolling();
      }
    },
    startPolling() {
      this.stopPolling();
      this.refreshTimer = setTimeout(() => {
        this.loadSpaces();
      }, 10000);
    },
    stopPolling() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    groupReservationsByDate(reservations) {
      return reservations.reduce((acc, reservation) => {
        const dateKey = reservation.start_date.split("T")[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(reservation);
        return acc;
      }, {});
    },
    handleSelectionUpdate({label, firstCell, selectedRange}) {
      if (!this.authStore.user && !this.allowGuestSelection && (firstCell || selectedRange)) {
        this.collisionAlert = {
          show: true,
          title: 'Wymagane logowanie',
          message: 'Musisz się zalogować aby zarezerwować stanowisko!'
        };
        return;
      }

      this.globalActiveSection = label;
      this.globalFirstCell = firstCell;
      this.globalSelectedRange = selectedRange;

      // Reset timer on selection interaction to prevent jumpy UI during selection
      this.startPolling();
    },
    checkSelectionCollision() {
      if (!this.globalSelectedRange || !this.globalActiveSection) return;

      const activeSpace = this.spaces[this.globalSelectedRange.spaceIdx];
      if (!activeSpace) return;

      // Extract YYYY-MM-DD from active label
      const dateMatch = this.globalActiveSection.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (!dateMatch) return;
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;

      const dayReservations = activeSpace.reservations[dateKey];
      if (!dayReservations) return;

      // Helper to format slot index to time (matches logic in DaySection)
      const formatTime = (slotIdx) => {
        const h = Math.floor((slotIdx * 30) / 60) + this.startHour;
        const m = (slotIdx * 30) % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };

      const rangeStart = formatTime(this.globalSelectedRange.from);
      // End time is start of next slot
      const rangeEnd = formatTime(this.globalSelectedRange.to + 1);

      const isColliding = dayReservations.some(res => {
        const resStart = new Date(res.start_date);
        const resEnd = new Date(res.end_date);

        // Apply same cooldown logic for collision detection
        const startWithCooldown = new Date(resStart.getTime() - this.cooldownMinutes * 60000);
        const endWithCooldown = new Date(resEnd.getTime() + this.cooldownMinutes * 60000);

        const formatLocal = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        const s = formatLocal(startWithCooldown);
        const e = formatLocal(endWithCooldown);

        // Standard overlap check
        return rangeStart < e && rangeEnd > s;
      });

      if (isColliding) {
        this.handleSelectionUpdate({label: null, firstCell: null, selectedRange: null});
        this.collisionAlert = {
          show: true,
          title: 'Termin zajęty',
          message: 'Niestety ktoś zarezerwował już ten okres! Wybierz ponownie godziny rezerwacji.'
        };
      }
    },
    handleReservationCreated() {
      this.loadSpaces();
    }
  },
  mounted() {
    this.loadSpaces();
  },
  beforeUnmount() {
    this.stopPolling();
  }
}
</script>

<style scoped>
[data-bs-theme="light"] .bg-pricing {
  background-color: #71a2c0 !important;
}

[data-bs-theme="dark"] .bg-pricing {
  background-color: #3a6580 !important;
}

[data-bs-theme="light"] .bg-price-hour {
  background-color: rgb(184, 168, 131) !important;
  border-color: rgb(120 84 48) !important;
  text-shadow: 0 0 5px black;
}

[data-bs-theme="light"] .bg-price-value {
  background-color: #797868 !important;
}

.week-view {
  background: transparent;
}

.day-section-wrapper {
  width: 100%;
}

.legend-info .slot-user-reserved {
  background-color: rgb(51 106 67) !important;
}
</style>